import React from 'react'
import { Translate, I18n } from 'react-redux-i18n'
import {
  AutosugguestTextFieldGroup,
  TextFieldGroup,
  NativeDateTimeFieldGroup,
  NativeSelectFieldGroup,
  TextAreaFieldGroup,
} from './CheckInFormFields'

const CheckInForm = ({
  serviceTxn,
  onFieldChange,
  onServiceTxnSelect,
  errors,
  onSubmit,
  clearForm,
  submitted,
  loadServiceTxns,
  travelClassOptions,
  touch,
  pristine,
  checkoutable,
  isCheckedOut,
  openCheckoutModal,
  renderInfo,
  onCameraScan,
  onKeyboardScan,
  kioskServiceInfo,
  isShowProductSelect,
  cancelable,
  openCancelModal,
  isSubmitting,
  openProductModal,
  selectedProduct,
}) => {
  const { hasCamera, isDesktop } = require('../../utils/deviceDetection')
  const showCameraButton = hasCamera()
  const showKeyboardButton = isDesktop()

  return (
    <div className="relative pb-32">
      <form className="kiosk-form" onSubmit={onSubmit}>
        {renderInfo && renderInfo()}

        {/* Form Fields - Multi-column layout for wide screens */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 space-y-6">
          {/* Product Selection - Full Width */}
          {isShowProductSelect && (
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Translate value="checkInScene.form.productId" />
                {(touch.productId || !pristine.productId) &&
                  errors.productId && (
                    <span className="ml-2 text-red-600">
                      <svg
                        className="inline w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  )}
              </label>
              <button
                type="button"
                onClick={openProductModal}
                className="w-full h-12 px-4 text-base text-left bg-white border border-gray-300 rounded-lg hover:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors flex items-center justify-between group"
              >
                <span
                  className={
                    selectedProduct ? 'text-gray-900' : 'text-gray-500'
                  }
                >
                  {selectedProduct ? selectedProduct.name : 'Select Product...'}
                </span>
                <svg
                  className="w-5 h-5 text-gray-400 group-hover:text-primary-600 transition-colors"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          )}

          {/* Guest Information Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4">
            {/* Left Column */}
            <div className="space-y-4">
              <AutosugguestTextFieldGroup
                name="customerLastName"
                i18n="checkInScene.form.customerLastName"
                value={serviceTxn.customerLastName}
                onChange={onFieldChange}
                error={errors.customerLastName}
                loadSuggestions={loadServiceTxns}
                getSuggestionValue={(serviceTxn) => serviceTxn.customerLastName}
                onSuggestionSelected={onServiceTxnSelect}
                renderSuggestion={renderMainPaxFullNameSuggestion}
                submitted={submitted}
                touched={touch.customerLastName}
                pristine={pristine.customerLastName}
              />

              <AutosugguestTextFieldGroup
                name="customerFirstName"
                i18n="checkInScene.form.customerFirstName"
                value={serviceTxn.customerFirstName}
                onChange={onFieldChange}
                error={errors.customerFirstName}
                loadSuggestions={loadServiceTxns}
                getSuggestionValue={(serviceTxn) =>
                  serviceTxn.customerFirstName
                }
                onSuggestionSelected={onServiceTxnSelect}
                renderSuggestion={renderMainPaxFullNameSuggestion}
                submitted={submitted}
                touched={touch.customerFirstName}
                pristine={pristine.customerFirstName}
              />

              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-7">
                  <TextFieldGroup
                    name="flightNumber"
                    i18n="checkInScene.form.flightNumber"
                    value={serviceTxn.flightNumber}
                    onChange={onFieldChange}
                    error={errors.flightNumber}
                    submitted={submitted}
                    touched={touch.flightNumber}
                    pristine={pristine.flightNumber}
                  />
                </div>
                <div className="col-span-5">
                  <TextFieldGroup
                    name="seatNumber"
                    i18n="checkInScene.form.seatNumber"
                    value={serviceTxn.seatNumber}
                    onChange={onFieldChange}
                    error={errors.seatNumber}
                    submitted={submitted}
                    touched={touch.seatNumber}
                    pristine={pristine.seatNumber}
                  />
                </div>
              </div>

              <NativeSelectFieldGroup
                name="travelClass"
                touchedServiceTxn
                i18n="checkInScene.form.travelClass"
                value={serviceTxn.travelClass}
                onChange={onFieldChange}
                error={errors.travelClass}
                submitted={submitted}
                options={travelClassOptions}
                touched={touch.travelClass}
                pristine={pristine.travelClass}
              />
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              <NativeDateTimeFieldGroup
                name="serviceInfo.inTime"
                i18n="checkInScene.form.serviceDateTime"
                value={serviceTxn.serviceInfo.inTime}
                onChange={onFieldChange}
                error={errors.serviceInfo.inTime}
                submitted={submitted}
                touched={touch.serviceInfo.inTime}
                pristine={pristine.serviceInfo.inTime}
                disabled={checkoutable || isCheckedOut}
              />

              <NativeDateTimeFieldGroup
                name="serviceInfo.stdTime"
                i18n="checkInScene.form.stdTime"
                value={serviceTxn.serviceInfo.stdTime}
                onChange={onFieldChange}
                error={errors.serviceInfo.stdTime}
                submitted={submitted}
                touched={touch.serviceInfo.stdTime}
                pristine={pristine.serviceInfo.stdTime}
                disabled={checkoutable || isCheckedOut}
              />

              <TextFieldGroup
                name="reference1"
                i18n="checkInScene.form.reference1"
                value={serviceTxn.reference1}
                onChange={onFieldChange}
                error={errors.reference1}
                submitted={submitted}
                touched={touch.reference1}
                pristine={pristine.reference1}
              />

              <TextFieldGroup
                name="reference2"
                i18n="checkInScene.form.reference2"
                value={serviceTxn.reference2}
                onChange={onFieldChange}
                error={errors.reference2}
                submitted={submitted}
                touched={touch.reference2}
                pristine={pristine.reference2}
              />
            </div>
          </div>

          {/* Guest Count Section - Single Row */}
          <div className="border-t border-gray-100 pt-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <TextFieldGroup
                  name="serviceInfo.guestPaxAdult"
                  i18n="checkInScene.form.guestPaxAdultFull"
                  value={serviceTxn.serviceInfo.guestPaxAdult}
                  onChange={onFieldChange}
                  error={errors.serviceInfo.guestPaxAdult}
                  submitted={submitted}
                  type="number"
                  touched={touch.serviceInfo.guestPaxAdult}
                  pristine={pristine.serviceInfo.guestPaxAdult}
                />
              </div>
              <div>
                <TextFieldGroup
                  name="serviceInfo.guestPaxChild"
                  i18n="checkInScene.form.guestPaxChildFull"
                  value={serviceTxn.serviceInfo.guestPaxChild}
                  onChange={onFieldChange}
                  type="number"
                  error={errors.serviceInfo.guestPaxChild}
                  submitted={submitted}
                  touched={touch.serviceInfo.guestPaxChild}
                  pristine={pristine.serviceInfo.guestPaxChild}
                />
              </div>
              <div>
                <TextFieldGroup
                  name="serviceInfo.guestPaxInfant"
                  i18n="checkInScene.form.guestPaxInfantFull"
                  value={serviceTxn.serviceInfo.guestPaxInfant}
                  onChange={onFieldChange}
                  error={errors.serviceInfo.guestPaxInfant}
                  submitted={submitted}
                  type="number"
                  touched={touch.serviceInfo.guestPaxInfant}
                  pristine={pristine.serviceInfo.guestPaxInfant}
                />
              </div>
              <div>
                <TextFieldGroup
                  name="serviceInfo.luggageAmount"
                  i18n="checkInScene.form.luggageAmount"
                  value={serviceTxn.serviceInfo.luggageAmount}
                  onChange={onFieldChange}
                  error={errors.serviceInfo.luggageAmount}
                  submitted={submitted}
                  type="number"
                  touched={touch.serviceInfo.luggageAmount}
                  pristine={pristine.serviceInfo.luggageAmount}
                />
              </div>
            </div>
          </div>

          {/* Notes Section - Full Width */}
          <div className="border-t border-gray-100 pt-6 space-y-5">
            <TextAreaFieldGroup
              name="remark"
              i18n="checkInScene.form.remark"
              value={serviceTxn.remark}
              onChange={onFieldChange}
              error={errors.remark}
              submitted={submitted}
              touched={touch.remark}
              pristine={pristine.remark}
            />

            <TextAreaFieldGroup
              name="serviceInfo.note"
              i18n="checkInScene.form.note"
              value={serviceTxn.serviceInfo.note}
              onChange={onFieldChange}
              error={errors.serviceInfo.note}
              submitted={submitted}
              touched={touch.serviceInfo.note}
              pristine={pristine.serviceInfo.note}
            />
          </div>
        </div>
      </form>

      {/* Sticky Action Buttons at Bottom with Blur Background */}
      <div className="fixed bottom-0 left-0 md:left-20 right-0 z-40 border-t border-gray-200 shadow-lg backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex flex-wrap gap-2 justify-center items-center">
            {checkoutable && (
              <button
                type="button"
                onClick={openCheckoutModal}
                className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors shadow-md"
              >
                <Translate value="action.checkout" />
              </button>
            )}
            {cancelable && (
              <button
                type="button"
                onClick={openCancelModal}
                className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors shadow-md"
              >
                <Translate value="action.cancel" />
              </button>
            )}
            <button
              type="button"
              onClick={onSubmit}
              disabled={isSubmitting}
              className="px-8 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors shadow-md"
            >
              <Translate
                value={
                  !checkoutable && !isCheckedOut
                    ? 'action.checkin'
                    : 'action.update'
                }
              />
            </button>
            <button
              type="button"
              onClick={clearForm}
              className="px-6 py-3 bg-white/60 backdrop-blur-sm hover:bg-white/80 text-gray-700 font-medium rounded-lg transition-colors shadow-md border border-gray-300"
            >
              <Translate value="actions.clear" />
            </button>
            {showCameraButton && (
              <button
                type="button"
                onClick={onCameraScan}
                className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors shadow-md flex items-center"
              >
                <svg
                  className="w-5 h-5 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                <Translate value="actions.scan" />
              </button>
            )}
            {showKeyboardButton && (
              <button
                type="button"
                onClick={onKeyboardScan}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors shadow-md flex items-center"
              >
                <svg
                  className="w-5 h-5 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"
                  />
                </svg>
                <Translate value="actions.scan" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const renderMainPaxFullNameSuggestion = (serviceTxn) => (
  <div>
    <div>{serviceTxn.reservation.confirmationNumber}</div>
    <div>{serviceTxn.customerName}</div>
  </div>
)

export default CheckInForm
